import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const whyChoose = [
  { icon: "fas fa-door-open", title: "Spacious interiors with multiple rooms" },
  { icon: "fas fa-people-arrows", title: "Separate areas for meetings, operations, and management" },
  { icon: "fas fa-house-user", title: "Private entrances for clients and staff" },
  { icon: "fas fa-square-parking", title: "Parking availability for convenience" },
  { icon: "fas fa-briefcase", title: "A professional yet welcoming setting" },
];

const rentalSteps = [
  ["Requirement Analysis", "We understand your business type, space needs, and preferred location."],
  ["Property Shortlisting", "You receive options that match your criteria and budget."],
  ["Site Visits", "We arrange viewings so you can assess layout, accessibility, and suitability."],
  ["Agreement and Finalization", "We assist with negotiations, documentation, and lease agreements."],
];

const faqs = [
  {
    icon: "fas fa-building",
    question: "What is a commercial villa in Abu Dhabi?",
    answer: "A commercial villa is a villa approved for commercial purposes.",
  },
  {
    icon: "fas fa-id-card",
    question: "Who can rent a commercial villa in Abu Dhabi?",
    answer: "Any business house with valid licenses can rent commercial villas.",
  },
  {
    icon: "fas fa-coins",
    question: "What is the average rent for a commercial villa in Abu Dhabi?",
    answer: "Rental prices vary based on location, size, and facilities. Premium areas usually have higher rents compared to emerging business zones.",
  },
  {
    icon: "fas fa-paint-roller",
    question: "Can I modify the interior of a commercial villa?",
    answer: "Yes. You can do minor modifications after discussions with the property owner.",
  },
  {
    icon: "fas fa-briefcase-medical",
    question: "Are commercial villas suitable for clinics or training centers?",
    answer: "Yes. They are.",
  },
  {
    icon: "far fa-clock",
    question: "How long does it take to rent a commercial villa in Abu Dhabi?",
    answer: "The process can take from a few days to a few weeks, depending on property selection, negotiations, and documentation.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Commercial Villa for Rent in Abu Dhabi", item: "https://nvrealestate.ae/commercial-villa-for-rent-abu-dhabi" },
  ],
};

function CommercialVillaForRentAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Commercial Villa for Rent in Abu Dhabi – Prime Spaces</title>
        <meta
          name="description"
          content="Are you searching for the best commercial villa for rent in Abu Dhabi? NV Real Estate, the most trusted real estate company in UAE, can help you rent a commercial villa in Abu Dhabi. Connect now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/commercial-villa-for-rent-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi commercial leasing specialists</span>
              <h1 id="villa-page-title">Commercial Villa for Rent in Abu Dhabi – Flexible Spaces for Growing Businesses</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Rent&city=&developer=&developer_name=&project=&project_name=&property_type=Commercial+Villa&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore properties
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/commercial-villa-for-rent-abu-dhabi/02.png"
                alt="Commercial villas for rent in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Business-ready spaces</strong>
                <span>Matched to your operational needs</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Commercial Villa for Rent in Abu Dhabi – Flexible Spaces for Growing Businesses</h2>
              <p>
                Nova Vision Real Estate is committed to helping you find the best commercial villa
                for <Link to="/archive-properties/one-bedroom-flat-abu-dhabi/">rent in Abu Dhabi</Link> because
                we understand it very well that selecting the right commercial space plays a
                critical role in your business's fate. We have access to all premium commercial
                villas in Abu Dhabi, and based on your specific business requirements, we can help
                you find the right villa that can support your business operations, reflect your
                brand, and offer room to grow.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-choose-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why a commercial villa</span>
              <h2 id="why-choose-heading">Why Choose a Commercial Villa in Abu Dhabi?</h2>
              <p>
                Businesses prefer commercial villas in Abu Dhabi for many reasons, and some of them
                are here for you.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {whyChoose.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">The rental process</span>
              <h2 id="villa-process-title">A Simple and Efficient Rental Process</h2>
              <p>
                Renting a commercial property should be clear and structured. Our approach ensures
                you move forward without confusion.
              </p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {rentalSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
              <p className="villa-process__closing">This process saves your time and helps you make confident decisions.</p>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Top Reasons to Choose NV Real Estate for a Commercial Villa in Abu Dhabi</h2>
                <p>
                  Business owners in Abu Dhabi trust us for renting business spaces for them because
                  they know we can provide them with the best solutions. Here are some of the top
                  reasons to choose our commercial villa in Abu Dhabi.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  Call us now to discuss your requirements and get the best commercial villa for
                  your needs.
                </p>
              </div>
              <ul>
                <li>We are a team of highly experienced real estate professionals.</li>
                <li>We have our connection with all the top developers in the city.</li>
                <li>We can provide the best property matching your needs.</li>
                <li>We have already served so many people in Abu Dhabi.</li>
                <li>Our clients are very satisfied with our services.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Renter questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your commercial villa search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`commercial-villa-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`commercial-villa-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-building" /><div><strong>Curated Spaces</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Lease Support</strong><span>Offer to Handover</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email your
              requirements to <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default CommercialVillaForRentAbuDhabi;